// src/pages/Profile/Order/MyOrders.js
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaShoppingBag } from "react-icons/fa";
import { ChevronRight } from "lucide-react";
import OrderDetails from "./OrderDetails";
import { fetchOrders } from "../../../services/Profile/Order/Order";

import fallbackImg from "../../assets/logo/appstore.png";

export default function MyOrders() {
  const user = JSON.parse(localStorage.getItem("medarphaUser"));
  const userId = user?.id || user?.UserId || user?.userId;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (!userId) return;

    const loadOrders = async () => {
      setLoading(true);
      const res = await fetchOrders(userId);

      if (res.ok) {
        if (res.data.length > 0) {
          const formatted = res.data.map((order) => {
            let items = [];
            let totalQty = 0;

            try {
              items = JSON.parse(order.itemsJson || "[]");
              totalQty = items.reduce(
                (sum, item) => sum + (item.Quantity || 0),
                0
              );
            } catch (err) {
              console.error("JSON parse error:", err);
            }

            return {
              ...order,
              items,
              totalQuantity: totalQty,
            };
          });

          setOrders(formatted);
        } else {
          toast.info("No orders found!");
        }
      } else {
        toast.error(res.error);
      }

      setLoading(false);
    };

    loadOrders();
  }, [userId]);

  const getStatusStyle = (status) => {
    switch (status) {
      case "Delivered":
        return { background: "#e8f5e9", color: "#2e7d32" };
      case "Scheduled":
        return { background: "#fff8e1", color: "#f57c00" };
      case "Cancelled":
        return { background: "#ffebee", color: "#c62828" };
      default:
        return { background: "#e3f2fd", color: "#1565c0" };
    }
  };

  return (
    <div style={{ fontFamily: "'Roboto', sans-serif" }}>

      {/* HEADER */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: 4,
          padding: "18px 24px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <FaShoppingBag size={18} color="#2874f0" />
        <span style={{ fontSize: 20, fontWeight: 700, color: "#212121" }}>
          My Orders
        </span>
      </div>

      {/* LOADING SKELETONS */}
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 4,
                padding: "20px 24px",
                display: "flex",
                gap: 16,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 3,
                  background: "#f0f0f0",
                  flexShrink: 0,
                  animation: "skPulse 1.4s ease infinite",
                }}
              />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ height: 14, width: "40%", background: "#f0f0f0", borderRadius: 3, animation: "skPulse 1.4s ease infinite" }} />
                <div style={{ height: 16, width: "65%", background: "#f0f0f0", borderRadius: 3, animation: "skPulse 1.4s ease infinite" }} />
                <div style={{ height: 13, width: "30%", background: "#f0f0f0", borderRadius: 3, animation: "skPulse 1.4s ease infinite" }} />
              </div>
            </div>
          ))}
        </div>

      ) : orders.length > 0 ? (

        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {orders.map((order, idx) => (
            <div
              key={order.orderId}
              onClick={() => {
                setSelectedOrder(order);
                setOpenDrawer(true);
              }}
              style={{
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderTop: idx === 0 ? "1px solid #e0e0e0" : "none",
                cursor: "pointer",
                padding: "20px 24px",
                display: "flex",
                alignItems: "center",
                gap: 18,
                transition: "background 0.12s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f8f9ff"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}
            >
              {/* IMAGE */}
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 3,
                  border: "1px solid #e0e0e0",
                  overflow: "hidden",
                  flexShrink: 0,
                  background: "#f5f5f5",
                }}
              >
                <img
                  src={fallbackImg}
                  alt="product"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>

              {/* CONTENT */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Order number + date row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#212121" }}>
                    {order.orderNumber}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      color: "#878787",
                      background: "#f5f5f5",
                      padding: "2px 10px",
                      borderRadius: 2,
                    }}
                  >
                    {new Date(order.orderDate).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>

                {/* Items count + amount */}
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: "#878787" }}>
                    {order.totalQuantity} {order.totalQuantity === 1 ? "Item" : "Items"}
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#212121" }}>
                    ₹{order.finalAmount || 0}
                  </span>
                </div>

                {/* Status badge */}
                <span
                  style={{
                    display: "inline-block",
                    padding: "3px 12px",
                    borderRadius: 2,
                    fontSize: 12,
                    fontWeight: 600,
                    ...getStatusStyle(order.status),
                  }}
                >
                  {order.status}
                </span>
              </div>

              {/* ARROW */}
              <ChevronRight size={20} style={{ color: "#ccc", flexShrink: 0 }} />
            </div>
          ))}
        </div>

      ) : (
        /* EMPTY STATE */
        <div
          style={{
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: 4,
            padding: "60px 24px",
            textAlign: "center",
          }}
        >
          <FaShoppingBag size={48} color="#e0e0e0" style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 18, fontWeight: 600, color: "#212121", marginBottom: 8 }}>
            No Orders Yet
          </div>
          <div style={{ fontSize: 14, color: "#878787" }}>
            You haven't placed any orders. Start shopping!
          </div>
        </div>
      )}

      {/* DRAWER */}
      {openDrawer && (
        <OrderDetails
          order={selectedOrder}
          onClose={() => setOpenDrawer(false)}
        />
      )}

      <style>{`
        @keyframes skPulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.45; }
        }
      `}</style>
    </div>
  );
}
